'use client';

import React from 'react';
import type { MenuProps as AriaMenuProps } from 'react-aria-components';
import { Menu as AriaMenu } from 'react-aria-components';
import { cx } from '@milanote/roma/utils/cx';
import { CommandMenuFooter } from './command-menu-footer';
import { CommandMenuHeaderBase, CommandMenuHeader } from './command-menu-header';
import { CommandMenuItem, CommandMenuItemStructuredBase } from './command-menu-item';
import { CustomCommandMenuItem } from './command-menu-item-custom';
import { CommandMenuItemBase } from './command-menu-item-base';
import { CommandMenuSeparatorBase, CommandMenuSeparator } from './command-menu-separator';
import { CommandMenuSubmenu, CommandMenuSubmenuPopover } from './command-menu-submenu';
import type { CommandMenuSize } from './command-menu.types';
import { CommandMenuContext } from './command-menu-context';

const COMMAND_MENU_DISPLAY_NAME = 'RomaCommandMenu';
const COMMAND_MENU_COLLECTION_DISPLAY_NAME = 'RomaCommandMenuCollection';

export type CommandMenuProps<T extends object = object> = Omit<AriaMenuProps<T>, 'className'> & {
    size?: CommandMenuSize;
    className?: AriaMenuProps<T>['className'];
};

const CommandMenuShell = <T extends object>({ size = 'sm', className, ...props }: CommandMenuProps<T>) => (
    <CommandMenuContext.Provider value={{ size }}>
        <AriaMenu
            {...props}
            className={(state) =>
                cx(
                    COMMAND_MENU_COLLECTION_DISPLAY_NAME,
                    'w-full outline-hidden',
                    typeof className === 'function' ? className(state) : className,
                )
            }
        />
    </CommandMenuContext.Provider>
);

CommandMenuShell.displayName = COMMAND_MENU_DISPLAY_NAME;

type CommandMenuBaseComponents = {
    Item: typeof CommandMenuItemBase;
    ItemStructured: typeof CommandMenuItemStructuredBase;
    Header: typeof CommandMenuHeaderBase;
    Separator: typeof CommandMenuSeparatorBase;
};

export const CommandMenu = CommandMenuShell as typeof CommandMenuShell & {
    Base: CommandMenuBaseComponents;
    Header: typeof CommandMenuHeader;
    Footer: typeof CommandMenuFooter;
    Item: typeof CommandMenuItem;
    CustomItem: typeof CustomCommandMenuItem;
    Separator: typeof CommandMenuSeparator;
    Submenu: typeof CommandMenuSubmenu;
    SubmenuPopover: typeof CommandMenuSubmenuPopover;
};

CommandMenu.Base = {
    Item: CommandMenuItemBase,
    ItemStructured: CommandMenuItemStructuredBase,
    Header: CommandMenuHeaderBase,
    Separator: CommandMenuSeparatorBase,
};
CommandMenu.Header = CommandMenuHeader;
CommandMenu.Footer = CommandMenuFooter;
CommandMenu.Item = CommandMenuItem;
CommandMenu.CustomItem = CustomCommandMenuItem;
CommandMenu.Separator = CommandMenuSeparator;
CommandMenu.Submenu = CommandMenuSubmenu;
CommandMenu.SubmenuPopover = CommandMenuSubmenuPopover;

export type { CommandMenuFooterProps } from './command-menu-footer';
export type { CommandMenuHeaderBaseProps, CommandMenuHeaderProps } from './command-menu-header';
export type { CommandMenuItemStructuredBaseProps, CommandMenuItemProps } from './command-menu-item';
export type { CommandMenuItemRootBaseProps } from './command-menu-item-base';
export type { CustomCommandMenuItemProps } from './command-menu-item-custom';
export type { CommandMenuSeparatorBaseProps, CommandMenuSeparatorProps } from './command-menu-separator';
export type { CommandMenuSubmenuProps, CommandMenuSubmenuPopoverProps } from './command-menu-submenu';
